import { useState, useCallback } from "react";
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
} from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { Link, useNavigate } from "@tanstack/react-router";
import Inventory from "@mui/icons-material/Inventory";
import Settings from "@mui/icons-material/Settings";
import People from "@mui/icons-material/People";
import Payment from "@mui/icons-material/Payment";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import Person from "@mui/icons-material/Person";
import Business from "@mui/icons-material/Business";
import Download from "@mui/icons-material/Download";
import Help from "@mui/icons-material/Help";
import Logout from "@mui/icons-material/Logout";
import Translate from "@mui/icons-material/Translate";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";

interface BottomNavProps {
  currentOrganization: {
    id: string;
    name: string;
  } | null;
  currentPath: string | undefined;
  onOrgMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
}

export default function BottomNav({
  currentOrganization,
  currentPath,
  onOrgMenuOpen,
}: BottomNavProps) {
  const { t } = useTranslation("nav");
  const { currentLanguage, changeLanguage } = useLanguage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const languages = [
    { code: "en" as const, label: "English" },
    { code: "fr" as const, label: "Français" },
  ];

  const navItems = [
    {
      label: t("bottomNav.projects"),
      icon: <Inventory />,
      to: "/projects",
      match: (path: string) => path.startsWith("/projects"),
    },
    {
      label: t("bottomNav.settings"),
      icon: <Settings />,
      to: currentOrganization
        ? `/organizations/${currentOrganization.id}`
        : "/projects",
      match: (path: string) =>
        !!currentOrganization &&
        path === `/organizations/${currentOrganization.id}`,
    },
    {
      label: t("bottomNav.members"),
      icon: <People />,
      to: currentOrganization
        ? `/organizations/${currentOrganization.id}/members`
        : "/projects",
      match: (path: string) =>
        !!currentOrganization &&
        path === `/organizations/${currentOrganization.id}/members`,
    },
    {
      label: t("bottomNav.billing"),
      icon: <Payment />,
      to: "/billing",
      match: (path: string) => path === "/billing",
    },
  ];

  const reducedMotion = useReducedMotion();

  const handleDrawerDragEnd = useCallback(
    (
      _event: MouseEvent | TouchEvent | PointerEvent,
      info: { offset: { y: number } },
    ) => {
      if (info.offset.y > 100) {
        setDrawerOpen(false);
      }
    },
    [],
  );

  const isMoreActive =
    currentPath === "/profile" ||
    currentPath === "/download" ||
    currentPath === "/help" ||
    currentPath === "/logout";

  const activeIndex = currentPath
    ? navItems.findIndex((item) => item.match(currentPath))
    : -1;

  const bottomNavValue = isMoreActive ? 4 : activeIndex >= 0 ? activeIndex : false;

  return (
    <>
      <Paper
        component="nav"
        aria-label={t("bottomNav.mainNav")}
        elevation={8}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderTop: 1,
          borderColor: "divider",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <BottomNavigation
          value={bottomNavValue}
          showLabels
          sx={{
            backgroundImage: (theme) =>
              `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
            height: 56,
            "& .MuiBottomNavigationAction-root": {
              minWidth: 48,
              minHeight: 48,
              color: "text.secondary",
              padding: "6px 0",
              "&.Mui-selected": {
                color: "primary.main",
              },
              "& .MuiBottomNavigationAction-label": {
                fontSize: "0.625rem",
                "&.Mui-selected": {
                  fontSize: "0.625rem",
                },
              },
            },
          }}
        >
          {navItems.map((item) => (
            <BottomNavigationAction
              key={item.label}
              label={item.label}
              icon={item.icon}
              component={Link}
              to={item.to as "/projects"}
              aria-current={
                currentPath && item.match(currentPath) ? "page" : undefined
              }
            />
          ))}
          <BottomNavigationAction
            label={t("bottomNav.more")}
            icon={<MoreHoriz />}
            onClick={() => setDrawerOpen(true)}
            aria-current={isMoreActive ? "page" : undefined}
          />
        </BottomNavigation>
      </Paper>

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            bgcolor: "background.paper",
            maxHeight: "60vh",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          },
        }}
      >
        <motion.div
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.5 }}
          onDragEnd={handleDrawerDragEnd}
          transition={reducedMotion ? { duration: 0 } : undefined}
          style={{ touchAction: "pan-x" }}
        >
          {/* Drag handle indicator */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: 1,
              pb: 0.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 4,
                borderRadius: 2,
                bgcolor: "text.disabled",
              }}
            />
          </Box>
          <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
            <Typography variant="overline" color="text.secondary">
              {t("bottomNav.more")}
            </Typography>
          </Box>
        </motion.div>
        <List sx={{ overflowY: "auto", touchAction: "pan-y" }}>
          <ListItemButton
            component={Link}
            to="/profile"
            onClick={() => setDrawerOpen(false)}
            selected={currentPath === "/profile"}
          >
            <ListItemIcon>
              <Person />
            </ListItemIcon>
            <ListItemText primary={t("bottomNav.profile")} />
          </ListItemButton>

          <ListItemButton
            onClick={(e) => {
              setDrawerOpen(false);
              onOrgMenuOpen(e);
            }}
          >
            <ListItemIcon>
              <Business />
            </ListItemIcon>
            <ListItemText
              primary="Organisation"
              secondary={currentOrganization?.name}
            />
          </ListItemButton>

          <Divider sx={{ my: 0.5 }} />

          <ListItemButton
            component={Link}
            to="/download"
            onClick={() => setDrawerOpen(false)}
            selected={currentPath === "/download"}
          >
            <ListItemIcon>
              <Download />
            </ListItemIcon>
            <ListItemText primary={t("bottomNav.download")} />
          </ListItemButton>

          <ListItemButton
            component={Link}
            to="/help"
            onClick={() => setDrawerOpen(false)}
            selected={currentPath === "/help"}
          >
            <ListItemIcon>
              <Help />
            </ListItemIcon>
            <ListItemText primary={t("bottomNav.help")} />
          </ListItemButton>

          {languages.map((lang) => (
            <ListItemButton
              key={lang.code}
              selected={currentLanguage === lang.code}
              onClick={() => {
                changeLanguage(lang.code);
                setDrawerOpen(false);
              }}
              sx={{ pl: 4 }}
            >
              <ListItemIcon>
                <Translate />
              </ListItemIcon>
              <ListItemText primary={lang.label} />
            </ListItemButton>
          ))}

          <Divider sx={{ my: 0.5 }} />

          <ListItemButton
            onClick={() => {
              setDrawerOpen(false);
              navigate({ to: "/logout" as "/projects" });
            }}
          >
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <ListItemText primary={t("bottomNav.logout")} />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
}
